import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DocDuePnAlert } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { resetPendingDocsAlertAction } from 'src/redux/actions/documentationAction';
import { getDisplayString } from 'src/utils/getDaysBtDate';
const columns = [
  { id: 'alertType', label: 'ALERT TYPE' },
  { id: 'docType', label: 'DOCUMENT TYPE' },
  { id: 'dueDate', label: 'DUE DATE TO COMPLETE' },
  { id: 'therapist', label: 'THERAPIST' },
];

const PriorityHigh = () => {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 11.55V14.25M15 16.95H15.0068M21.75 14.25C21.75 17.9779 18.7279 21 15 21C11.2721 21 8.25 17.9779 8.25 14.25C8.25 10.5221 11.2721 7.5 15 7.5C18.7279 7.5 21.75 10.5221 21.75 14.25Z"
        stroke="#F04438"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const WarningAmber = () => {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.0003 12.7502V15.7502M15.0003 18.7502H15.0078M13.9618 8.91897L7.79313 19.5739C7.45097 20.1649 7.2799 20.4604 7.30518 20.7029C7.32724 20.9145 7.43806 21.1067 7.61008 21.2318C7.8073 21.3752 8.14875 21.3752 8.83164 21.3752H21.169C21.8519 21.3752 22.1933 21.3752 22.3905 21.2318C22.5625 21.1067 22.6734 20.9145 22.6954 20.7029C22.7207 20.4604 22.5496 20.1649 22.2075 19.5739L16.0388 8.91896C15.6979 8.33009 15.5274 8.03566 15.305 7.93677C15.111 7.85051 14.8896 7.85051 14.6956 7.93677C14.4732 8.03566 14.3027 8.33009 13.9618 8.91897Z"
        stroke="#F79009"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const MissingDocsList = () => {
  const missingDocsList = useSelector((state: any) => state?.checkPendingDocsAlert);
  const docSettings = useSelector((state: any) => state.dueDocSettingsReducer?.response?.data)
  const dispatch = useDispatch();
  const [isDocDuePNAlert] = useFeatureToggleWhenReady(DocDuePnAlert, 'FACILITY')
  const checkAlertType = (document: any) => {
    if (!document.submissionId) {
      return 'Missing Document';
    }
    if (
      document.signatureStatus === 'COMPLETED' &&
      document.supervisorId &&
      document.supervisorSignStatus === 'PENDING'
    ) {
      return 'Missing Supervisor Signature';
    }
    return 'Missing Therapist Signature';
  };

  const getTherapist = (document: any) => {
    if (!document.submissionId) {
      return document?.providerName ? document?.providerName : '';
    }
    if (
      document.signatureStatus === 'COMPLETED' &&
      document.supervisorId &&
      document.supervisorSignStatus === 'PENDING'
    ) {
      return document?.supervisorName ? document?.supervisorName : '';
    }
    return document?.providerName ? document?.providerName : '';
  };

  useEffect(() => {
    return () => {
      dispatch(resetPendingDocsAlertAction());
    };
  }, []);

  return (
    <Box sx={{ maxHeight: '400px', overflowY: 'auto', minWidth: 1200 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {columns?.map((column: any) => <TableCell key={column.id}>{column.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody sx={{ maxHeight: '300px', overflow: 'scroll' }}>
          {missingDocsList?.response?.map((document: any, index: number) => (
            <TableRow key={index}>
              <TableCell width={'300px'}>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'12px'}
                >
                  {document?.submissionId ? (
                    <Box
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: '#F790091F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <WarningAmber />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: '#F044381F',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PriorityHigh />
                    </Box>
                  )}

                  {checkAlertType(document)}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {document?.formType
                    ? document?.formType
                        ?.split('_')
                        .map((item: string) =>
                          ['PT', 'OT', 'SLP'].includes(item)
                            ? item
                            : item.slice(0, 1) + item.slice(1, item.length).toLowerCase()
                        )
                        .join(' ')
                        .replace('Reform', ' ')
                    : ''}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {document.dueDate
                      ? format(
                          new Date(
                            new Date(document.dueDate).valueOf() +
                              new Date(document.dueDate).getTimezoneOffset() * 60 * 1000
                          ),
                          'dd MMM, yyyy'
                        )
                      : '-'}
                  </Typography>
                </Box>
                {document.dueDate !== null && (
                  <Typography
                    sx={{ color: '#6C737F' }}
                    variant="body2"
                  >
                    {getDisplayString(document.dueDate, true, document, docSettings?.progressNoteDays, "RECHECK_ALERT", isDocDuePNAlert)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{getTherapist(document)}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MissingDocsList;
